import { type QueryKey, hashKey, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

interface PaginatedPage<T> {
	items: T[]
	nextCursor?: string
	hasMore: boolean
}

interface UsePaginatedQueryOptions<T> {
	queryKey: QueryKey
	fetchPage: (cursor?: string) => Promise<PaginatedPage<T>>
	getItemId: (item: T) => number | string
	enabled?: boolean
}

export function usePaginatedQuery<T>({
									 queryKey,
									 fetchPage,
									 getItemId,
									 enabled = true,
								 }: UsePaginatedQueryOptions<T>) {
	const queryClient = useQueryClient()
	const [items, setItems] = useState<T[]>([])
	const [cursor, setCursor] = useState<string | undefined>(undefined)
	const [hasNextPage, setHasNextPage] = useState(true)
	const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

	// Store queryKey in ref to avoid infinite loops in callbacks
	// and use hashKey for efficient change detection
	const queryKeyRef = useRef(queryKey)
	const queryKeyHashRef = useRef(hashKey(queryKey))

	// First page query (benefits from prefetch cache)
	const { data: firstPage, isLoading, error } = useQuery({
		queryKey,
		queryFn: () => fetchPage(undefined),
		enabled,
	})

	// Reset state when query key changes (section switch)
	useEffect(() => {
		const currentHash = hashKey(queryKey)
		if (currentHash !== queryKeyHashRef.current) {
			queryKeyRef.current = queryKey
			queryKeyHashRef.current = currentHash
			setItems([])
			setCursor(undefined)
			setHasNextPage(true)
		}
	}, [queryKey])

	// When first page loads, initialize state
	useEffect(() => {
		if (firstPage) {
			setItems(firstPage.items)
			setCursor(firstPage.nextCursor)
			setHasNextPage(firstPage.hasMore)
		}
	}, [firstPage])

	// Fetch next page manually
	const fetchNextPage = useCallback(async () => {
		if (!cursor || !hasNextPage || isFetchingNextPage) return

		setIsFetchingNextPage(true)
		try {
			const nextPage = await fetchPage(cursor)

			// Deduplicate when appending
			setItems(prev => {
				const existingIds = new Set(prev.map(getItemId))
				const newItems = nextPage.items.filter(item => !existingIds.has(getItemId(item)))
				return [...prev, ...newItems]
			})

			setCursor(nextPage.nextCursor)
			setHasNextPage(nextPage.hasMore)
		} finally {
			setIsFetchingNextPage(false)
		}
	}, [cursor, hasNextPage, isFetchingNextPage, fetchPage, getItemId])

	// Optimistically remove an item from local state
	const removeItem = useCallback((id: string | number) => {
		setItems(prev => prev.filter(item => getItemId(item) !== id))
	}, [getItemId])

	// Optimistically update an item in local state
	const updateItem = useCallback((id: string | number, updater: (item: T) => T) => {
		setItems(prev => prev.map(item =>
			getItemId(item) === id ? updater(item) : item,
		))
	}, [getItemId])

	// Prepend a new item to the top of the list
	const prependItem = useCallback((item: T) => {
		setItems(prev => [item, ...prev])
	}, [])

	// Invalidate the query cache to force a background refetch
	// Uses ref to avoid queryKey array in dependency list
	const invalidate = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: queryKeyRef.current })
	}, [queryClient])

	// Force an immediate refetch (stronger than invalidate - actually fetches now)
	// Uses ref to avoid queryKey array in dependency list
	const refetch = useCallback(() => {
		queryClient.refetchQueries({ queryKey: queryKeyRef.current })
	}, [queryClient])

	return {
		items,
		isLoading,
		error,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		removeItem,
		updateItem,
		prependItem,
		invalidate,
		refetch,
	}
}
